import { SnippetDb } from 'lib/cosmos/snippet/snippetDb.js';
import { map, of } from 'rxjs';
import { ListMySnippetsComponent } from 'src/app/modules/snippets/list-my-snippets.component.js';
import { SnippetFormComponent } from 'src/app/modules/snippets/snippet-form.component.js';
import { CoreAppComponentDependencies } from '../../core/core-app-dependencies.js';
import {
	ICoreRoutes,
	IPageMetadata,
	ISharedData,
	ITypePageCard,
	ITypeParentRoute,
	ITypeRoute,
	ITypeRouteAction,
	RouteInitializer
} from '../../pages/type-routes/type-route.models.js';

export interface ISnippetRoutes extends ICoreRoutes {
	readonly snippets: ITypeRoute<
		undefined,
		{
			readonly custom: ITypeRouteAction<undefined>;
			readonly new: ITypeRouteAction<undefined>;
		}
	>;
	readonly snippet: ITypeRoute<
		SnippetDb.ISnippetItem,
		{
			readonly edit: ITypeRouteAction<SnippetDb.ISnippetItem>;
		}
	>;
}

export const snippetRoutes: RouteInitializer<ISnippetRoutes> = {
	snippets: {
		mainPath: 'trainer',
		uniqueId: 'snippets',
		parentRoutes: (context) => [],
		loadSharedData: (context) => {
			return of(undefined);
		},
		menuItems: (context) => [],
		children: {
			custom: {
				uniqueId: `customSnippets`,
				component: ListMySnippetsComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Snippets`
					};
					return metadata;
				}
			},
			new: {
				parentRoutes: (context) => [
					{
						routerLink: context.dependencies.navigateService
							.actionRoute((routes) => routes.snippetRoutes.snippets.children.custom)
							.getUrl(),
						title: `Snippets`
					}
				],
				uniqueId: `newSnippet`,
				component: SnippetFormComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `New`
					};

					return metadata;
				}
			}
		}
	},
	snippet: {
		mainPath: 'trainer',
		uniqueId: 'snippet',
		parentRoutes: (context) => {
			const itemRoutes: ITypeParentRoute[] = [];

			return [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.snippetRoutes.snippets.children.custom)
						.getUrl(),
					title: `Snippets`
				},
				...itemRoutes
			];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<SnippetDb.ISnippetItem>((builder) =>
					builder.withApiRoute('snippet', 'getSingle').withId(context.routeData.primaryId).build()
				)
				.pipe(
					map((response) => {
						const shared: ISharedData<SnippetDb.ISnippetItem> = {
							data: response.data,
							linkedItems: response.linkedItems
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [
			{
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.snippetRoutes.snippet.children.edit, context.routeData.primaryId)
					.getUrl(),
				title: `Edit`,
				iconType: 'edit'
			}
		],
		children: {
			edit: {
				uniqueId: `EditSnippet`,
				component: SnippetFormComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Edit`,
						card: context.sharedData ? getCard(context.sharedData?.data, context.dependencies) : undefined
					};

					return metadata;
				}
			}
		}
	}
};

function getCard(snippet: SnippetDb.ISnippetItem, dependencies: CoreAppComponentDependencies): ITypePageCard {
	const subtitle: string | undefined = snippet.text;

	return {
		title: dependencies.coreAppService.getTitleCardConfig<SnippetDb.ISnippetItem>({
			updateSharedData: (title, sharedData) => {
				if (sharedData.data) {
					sharedData.data.system.name = title;
					sharedData.data.system.updated = dependencies.timeService.nowCosmosDb();
				}
				return sharedData;
			},
			item: snippet
		}),
		subtitle: subtitle,
		updated: snippet.system.updated,
		avatar: undefined,
		buttons: [
			{
				title: 'More',
				nestedButtons: [
					{
						title: 'Delete',
						confirm: {
							confirmMode: 'delete',
							text: `Are you sure to delete '${snippet.system.name}'?`
						},
						clickAsync: () => {
							return dependencies.httpService.deleteSingle('snippet', snippet.id).pipe(
								map((response) => {
									dependencies.notificationService.notifyDeleted('Snippet');

									dependencies.navigateService
										.actionRoute((appRoutes) => appRoutes.snippetRoutes.snippets.children.custom)
										.navigate();
								})
							);
						}
					}
				]
			}
		],
		layoutStyle: 'default'
	};
}
